import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { answerGuideQuestion } from "../utils/guideAssistantEngine";
import "./GuideAssistant.css";

const QUICK_ACTIONS = [
  { id: "profil", label: "Mon profil", to: "/profil" },
  { id: "amis", label: "Mes amis", to: "/amis" },
  { id: "annonces", label: "Mes annonces", to: "/annonces" },
  { id: "market", label: "Market", to: "/market" },
  { id: "forum", label: "Forum", to: "/forum" },
];

export default function GuideAssistant() {
  useSourceLanguage();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);
  const triggerRef = useRef(null);
  useEffect(() => {
    const showGuide = () => { triggerRef.current = document.activeElement; setOpen(true); };
    window.addEventListener("eternua:open-guide", showGuide);
    return () => window.removeEventListener("eternua:open-guide", showGuide);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const closeOnEscape = event => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      if (triggerRef.current?.isConnected) triggerRef.current.focus();
    };
  }, [open]);
  const [input, setInput] = useState("");
  const [lastQuestion, setLastQuestion] = useState("");
  const [pendingAction, setPendingAction] = useState(null);

  const answer = useMemo(
    () => answerGuideQuestion(lastQuestion, !!isLoggedIn),
    [lastQuestion, isLoggedIn]
  );

  const submit = (evt) => {
    evt.preventDefault();
    const q = String(input || "").trim();
    if (!q) return;
    setLastQuestion(q);
    setInput("");
  };

  const go = (item) => {
    if (!item?.route) return;
    if (item.requiresConfirmation) {
      setPendingAction(item);
      return;
    }
    const destination = item.route.startsWith("/") && !item.route.startsWith("//") ? item.route : "/";
    navigate(item.requiresAuth && !isLoggedIn ? `/login?next=${encodeURIComponent(destination)}` : destination);
  };

  if (!open) return null;

  return (
    <div className={"guide-assistant" + (open ? " guide-assistant--open" : "")}>
      {open ? (
        <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="guide-assistant__panel" role="dialog" aria-label={sourceAttribute("Concierge Eternua")}>
          <div className="guide-assistant__top">
            <div className="guide-assistant__title"><LocalizedText source={"Concierge Eternua"} /></div>
            <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]}
              type="button"
              className="guide-assistant__close"
              ref={closeRef}
              onClick={() => setOpen(false)}
              aria-label={sourceAttribute("Fermer le guide")}
            >
              ×
            </LocalizedElement>
          </div>

          <div className="guide-assistant__body">
            <div className="guide-assistant__bubble guide-assistant__bubble--assistant">
              {lastQuestion
                ? answer?.text
                : <LocalizedText source={"Décrivez votre besoin. Je vous oriente vers les rubriques utiles à partir de réponses préparées."} />}
            </div>

            {lastQuestion ? (
              <div className="guide-assistant__bubble guide-assistant__bubble--user">
                {lastQuestion}
              </div>
            ) : null}

            {answer?.route && !pendingAction ? (
              <button
                type="button"
                className="guide-assistant__go"
                onClick={() => go(answer)}
              >
                {answer.label || <LocalizedText source={"Ouvrir"} />}
              </button>
            ) : null}

            {pendingAction ? (
              <div className="guide-assistant__confirmation" role="alert">
                <strong><LocalizedText source={"Vérification avant de continuer"} /></strong>
                <p>{pendingAction.confirmation}</p>
                <div>
                  <button type="button" onClick={() => setPendingAction(null)}><LocalizedText source={"Revenir"} /></button>
                  <button
                    type="button"
                    onClick={() => {
                      const selected = { ...pendingAction, requiresConfirmation: false };
                      setPendingAction(null);
                      go(selected);
                    }}
                  ><LocalizedText source={"Confirmer et ouvrir"} /></button>
                </div>
              </div>
            ) : null}

            <form className="guide-assistant__form" onSubmit={submit}>
              <LocalizedElement elementType={"input"} copyAttributes={["placeholder"]}
                className="guide-assistant__input"
                value={input}
                onChange={(e) => setInput(String(e.target.value))}
                placeholder={sourceAttribute("Ex: Où voir mes annonces ?")}
              />
              <button type="submit" className="guide-assistant__send"><LocalizedText source={"Envoyer"} /></button>
            </form>

            <div className="guide-assistant__quick">
              {QUICK_ACTIONS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="guide-assistant__quickBtn"
                  onClick={() => navigate(a.to)}
                >
                  {<LocalizedText source={a.label} />}
                </button>
              ))}
            </div>

            {Array.isArray(answer?.suggestions) && answer.suggestions.length ? (
              <div className="guide-assistant__suggestions">
                {answer.suggestions.slice(0, 4).map((s) => (
                  <button
                    key={`suggest_${s.id}`}
                    type="button"
                    className="guide-assistant__suggestionBtn"
                    onClick={() => go(s)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </LocalizedElement>
      ) : null}


    </div>
  );
}
